import React, { useState, useMemo, lazy, Suspense } from 'react';
import {
  Box,
  Grid,
  Tabs,
  Tab,
  Paper,
  CircularProgress,
} from '@mui/material';
import PeopleIcon from '@mui/icons-material/People';
import PersonAddIcon from '@mui/icons-material/PersonAdd';
import FolderSharedIcon from '@mui/icons-material/FolderShared';
import CorporateFareIcon from '@mui/icons-material/CorporateFare';

import { useAuth } from '../../../Contexts/AuthContext';
import { useSimulation } from '../../../Contexts/SimulationContext';

// Components are code-split using React.lazy; loading behavior depends on how tab content is rendered
const ManageUsersImproved = lazy(() => import('./userManageImproved'));
const NewUser = lazy(() => import('./userCreate'));
const UserProjectAccess = lazy(() => import('./userProjectAccess'));
const NewOrganization = lazy(() => import('./organizationCreate'));
const DeleteOrganization = lazy(() => import('./organizationDelete'));

function TabPanel(props) {
  const { children, value, index, ...other } = props;

  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`admin-tabpanel-${index}`}
      aria-labelledby={`admin-tab-${index}`}
      {...other}
    >
      {value === index && (
        <Box sx={{ py: 3 }}>
          {children}
        </Box>
      )}
    </div>
  );
}

function a11yProps(index) {
  return {
    id: `admin-tab-${index}`,
    'aria-controls': `admin-tabpanel-${index}`,
  };
}

// Tab icons defined outside component to prevent recreation on each render
const TAB_ICONS = {
  manageUsers: <PeopleIcon />,
  addUser: <PersonAddIcon />,
  projectAccess: <FolderSharedIcon />,
  organizations: <CorporateFareIcon />,
};

// Loading fallback component
const TabLoading = () => (
  <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: 200 }}>
    <CircularProgress />
  </Box>
);

const Admin = () => {
  const auth = useAuth();
  const simulation = useSimulation();
  const { activeUser } = auth;
  const [activeTab, setActiveTab] = useState(0);

  const handleTabChange = (event, newValue) => {
    setActiveTab(newValue);
  };

  // Use effective role for tab visibility
  const effectiveRole = simulation.getEffectiveRole();
  const isSuperUser = effectiveRole === 0;
  
  // Tab configuration with labels and icons - memoized to prevent recreation on each render
  const tabs = useMemo(() => {
    const baseTabs = [
      { label: 'Manage Users', icon: TAB_ICONS.manageUsers },
      { label: 'Add User', icon: TAB_ICONS.addUser },
      { label: 'Project Access', icon: TAB_ICONS.projectAccess },
    ];
    
    // Add organization tab for super users
    if (isSuperUser) {
      baseTabs.push({ label: 'Organizations', icon: TAB_ICONS.organizations });
    }
    
    return baseTabs;
  }, [isSuperUser]);

  // Render tab content based on active tab - true lazy rendering
  const renderTabContent = (index) => {
    switch (index) {
      case 0:
        return <ManageUsersImproved />;
      case 1:
        return <NewUser />;
      case 2:
        return <UserProjectAccess />;
      case 3:
        return isSuperUser ? (
          <Box sx={{ width: '100%' }}>
            <Grid container spacing={4}>
              <Grid item xs={12}>
                <Paper elevation={1} sx={{ p: 3 }}>
                  <NewOrganization />
                </Paper>
              </Grid>
              <Grid item xs={12}>
                <Paper elevation={1} sx={{ p: 3 }}>
                  <DeleteOrganization />
                </Paper>
              </Grid>
            </Grid>
          </Box>
        ) : null;
      default:
        return null;
    }
  };

  return (
    <Box
      sx={{ 
        display: 'flex',
        flexDirection: 'column',
        width: '100%',
      }}
    >
      <Paper 
        elevation={0} 
        sx={{ 
          borderBottom: 1, 
          borderColor: 'divider',
          backgroundColor: 'transparent',
        }}
      >
        <Tabs 
          value={activeTab} 
          onChange={handleTabChange} 
          aria-label="Admin management tabs"
          variant="scrollable"
          scrollButtons="auto"
        >
          {tabs.map((tab, index) => (
            <Tab
              key={index}
              icon={tab.icon}
              iconPosition="start"
              label={tab.label}
              {...a11yProps(index)}
              sx={{
                minHeight: '48px',
                textTransform: 'none',
                fontSize: '0.9rem',
              }}
            />
          ))}
        </Tabs>
      </Paper>

      {tabs.map((tab, index) => (
        <TabPanel key={index} value={activeTab} index={index}>
          <Suspense fallback={<TabLoading />}>
            {activeTab === index && renderTabContent(index)}
          </Suspense>
        </TabPanel>
      ))}
    </Box>
  );
};

export default Admin;